import { LEAD_ENDPOINTS } from "../constants/leadEndpoints";

// The lead endpoints answer HTTP 200 with statusType FAILURE/ERROR for business errors
const unwrap = (data) => {
  if (data?.statusType && data.statusType !== "SUCCESS") {
    throw new Error(data.text || "Request failed");
  }
  return data;
};

export const createLeadService = (httpClient) => ({
  // Leads of the logged-in customer (owner resolved from the auth token, as on mobile)
  listMy: async ({ page_number = 1, page_size = 500, order_by = "DESC" } = {}) => {
    const response = await httpClient.post(LEAD_ENDPOINTS.listMy, { page_number, page_size, order_by });
    return unwrap(response.data);
  },

  getById: async (id) => {
    const response = await httpClient.get(LEAD_ENDPOINTS.details(id));
    return unwrap(response.data);
  },

  // Payload shape is the mobile one: { id?, title, description, budget, city, state, purpose: { id } }
  save: async ({ id, title, description, budget, city, state, purposeId }) => {
    const payload = {
      ...(id && { id: Number(id) }),
      title,
      description,
      budget: Number(budget),
      city,
      state,
      purpose: { id: Number(purposeId) },
    };
    const response = id
      ? await httpClient.put(LEAD_ENDPOINTS.edit, payload)
      : await httpClient.post(LEAD_ENDPOINTS.create, payload);
    return unwrap(response.data);
  },

  remove: async (id) => {
    const response = await httpClient.delete(LEAD_ENDPOINTS.remove(id));
    return unwrap(response.data);
  },

  // ---- notes (threaded through parent_id, as on mobile) ----
  listNotes: async (leadId) => {
    const response = await httpClient.post(LEAD_ENDPOINTS.notes, {
      page_number: 1,
      page_size: 100,
      order_by: "ASC",
      keyword_search: "",
      lead_id: leadId,
    });
    return unwrap(response.data);
  },

  addNote: async (leadId, note, parentId = 0) => {
    const response = await httpClient.post(LEAD_ENDPOINTS.addNote, {
      customerLead: { id: leadId },
      note,
      parent_id: parentId,
    });
    return unwrap(response.data);
  },

  editNote: async (noteObj, note) => {
    const response = await httpClient.put(LEAD_ENDPOINTS.editNote, {
      id: noteObj.id,
      note,
      parent_id: noteObj.parent_id,
      is_last: noteObj.is_last,
      customerLead: { id: noteObj.customerLead?.id },
      user: { id: noteObj.user?.id },
    });
    return unwrap(response.data);
  },

  removeNote: async (noteId) => {
    const response = await httpClient.delete(LEAD_ENDPOINTS.removeNote(noteId));
    return unwrap(response.data);
  },

  // ---- dropdown data (not hardcoded, same endpoints as mobile) ----
  fetchPurposes: async () => {
    const response = await httpClient.post(LEAD_ENDPOINTS.purposes, { page_number: 1, page_size: 50, order_by: "ASC" });
    return unwrap(response.data).customerLeadPurposes || [];
  },

  fetchStates: async (countryId = 1) => {
    const response = await httpClient.post(LEAD_ENDPOINTS.states(countryId), { page_number: 1, page_size: 100, order_by: "ASC" });
    return response.data?.states || [];
  },

  fetchCities: async (stateId) => {
    const response = await httpClient.post(LEAD_ENDPOINTS.cities(stateId), { page_number: 1, page_size: 1000, order_by: "ASC" });
    return response.data?.cities || [];
  },
});
